/**
 * Consent Banner & Modal Translations
 * Multi-language support for CMP
 */

export interface ConsentTranslations {
  banner: {
    title: string;
    description: string;
    acceptAll: string;
    reject: string;
    customize: string;
    privacyPolicy: string;
    cookiePolicy: string;
  };
  modal: {
    title: string;
    description: string;
    savePreferences: string;
    acceptAll: string;
    rejectAll: string;
    categories: {
      necessary: {
        title: string;
        description: string;
        details: string;
      };
      analytics: {
        title: string;
        description: string;
        details: string;
      };
      marketing: {
        title: string;
        description: string;
        details: string;
      };
      personalization: {
        title: string;
        description: string;
        details: string;
      };
    };
  };
  footer: {
    manageCookies: string;
    doNotSell: string;
  };
}

export const consentTranslations: Record<string, ConsentTranslations> = {
  en: {
    banner: {
      title: 'We value your privacy',
      description: 'We use cookies and similar technologies to enhance your experience, analyze site traffic, and for marketing purposes. You can manage your preferences or accept all cookies.',
      acceptAll: 'Accept All',
      reject: 'Reject',
      customize: 'Customize',
      privacyPolicy: 'Privacy Policy',
      cookiePolicy: 'Cookie Policy',
    },
    modal: {
      title: 'Cookie Preferences',
      description: 'Manage your cookie preferences below. You can enable or disable different types of cookies and save your preferences.',
      savePreferences: 'Save Preferences',
      acceptAll: 'Accept All',
      rejectAll: 'Reject All',
      categories: {
        necessary: {
          title: 'Strictly Necessary',
          description: 'These cookies are essential for the website to function properly and cannot be disabled.',
          details: 'Session management, security, load balancing. Duration: Session.',
        },
        analytics: {
          title: 'Analytics',
          description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
          details: 'Google Analytics 4. Duration: up to 2 years.',
        },
        marketing: {
          title: 'Marketing & Advertising',
          description: 'These cookies are used to track visitors across websites to display relevant advertisements.',
          details: 'Google Ads, Meta Pixel, LinkedIn. Duration: up to 2 years.',
        },
        personalization: {
          title: 'Personalization',
          description: 'These cookies allow the website to remember choices you make and provide enhanced features.',
          details: 'Language preferences, user settings. Duration: 1 year.',
        },
      },
    },
    footer: {
      manageCookies: 'Manage Cookies',
      doNotSell: 'Do Not Sell My Information',
    },
  },
  es: {
    banner: {
      title: 'Valoramos su privacidad',
      description: 'Utilizamos cookies y tecnologías similares para mejorar su experiencia, analizar el tráfico del sitio y con fines de marketing. Puede gestionar sus preferencias o aceptar todas las cookies.',
      acceptAll: 'Aceptar Todo',
      reject: 'Rechazar',
      customize: 'Personalizar',
      privacyPolicy: 'Política de Privacidad',
      cookiePolicy: 'Política de Cookies',
    },
    modal: {
      title: 'Preferencias de Cookies',
      description: 'Gestione sus preferencias de cookies a continuación. Puede habilitar o deshabilitar diferentes tipos de cookies y guardar sus preferencias.',
      savePreferences: 'Guardar Preferencias',
      acceptAll: 'Aceptar Todo',
      rejectAll: 'Rechazar Todo',
      categories: {
        necessary: {
          title: 'Estrictamente Necesarias',
          description: 'Estas cookies son esenciales para que el sitio web funcione correctamente y no se pueden desactivar.',
          details: 'Gestión de sesión, seguridad, balanceo de carga. Duración: Sesión.',
        },
        analytics: {
          title: 'Análisis',
          description: 'Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web recopilando información de forma anónima.',
          details: 'Google Analytics 4. Duración: hasta 2 años.',
        },
        marketing: {
          title: 'Marketing y Publicidad',
          description: 'Estas cookies se utilizan para rastrear visitantes en sitios web para mostrar anuncios relevantes.',
          details: 'Google Ads, Meta Pixel, LinkedIn. Duración: hasta 2 años.',
        },
        personalization: {
          title: 'Personalización',
          description: 'Estas cookies permiten que el sitio web recuerde las opciones que usted elige y proporcione funciones mejoradas.',
          details: 'Preferencias de idioma, configuración de usuario. Duración: 1 año.',
        },
      },
    },
    footer: {
      manageCookies: 'Gestionar Cookies',
      doNotSell: 'No Vender Mi Información',
    },
  },
  vi: {
    banner: {
      title: 'Chúng tôi tôn trọng quyền riêng tư của bạn',
      description: 'Chúng tôi sử dụng cookie và các công nghệ tương tự để nâng cao trải nghiệm của bạn, phân tích lưu lượng truy cập trang web và cho mục đích tiếp thị. Bạn có thể quản lý tùy chọn hoặc chấp nhận tất cả cookie.',
      acceptAll: 'Chấp nhận tất cả',
      reject: 'Từ chối',
      customize: 'Tùy chỉnh',
      privacyPolicy: 'Chính sách bảo mật',
      cookiePolicy: 'Chính sách Cookie',
    },
    modal: {
      title: 'Tùy chọn Cookie',
      description: 'Quản lý tùy chọn cookie của bạn bên dưới. Bạn có thể bật hoặc tắt các loại cookie khác nhau và lưu tùy chọn của bạn.',
      savePreferences: 'Lưu tùy chọn',
      acceptAll: 'Chấp nhận tất cả',
      rejectAll: 'Từ chối tất cả',
      categories: {
        necessary: {
          title: 'Cần thiết',
          description: 'Các cookie này cần thiết để trang web hoạt động đúng cách và không thể tắt được.',
          details: 'Quản lý phiên, bảo mật. Thời hạn: Phiên.',
        },
        analytics: {
          title: 'Phân tích',
          description: 'Các cookie này giúp chúng tôi hiểu cách khách truy cập tương tác với trang web bằng cách thu thập thông tin ẩn danh.',
          details: 'Google Analytics 4. Thời hạn: đến 2 năm.',
        },
        marketing: {
          title: 'Tiếp thị & Quảng cáo',
          description: 'Các cookie này được sử dụng để theo dõi khách truy cập trên các trang web để hiển thị quảng cáo phù hợp.',
          details: 'Google Ads, Meta Pixel, LinkedIn. Thời hạn: đến 2 năm.',
        },
        personalization: {
          title: 'Cá nhân hóa',
          description: 'Các cookie này cho phép trang web ghi nhớ các lựa chọn của bạn và cung cấp các tính năng nâng cao.',
          details: 'Tùy chọn ngôn ngữ, cài đặt người dùng. Thời hạn: 1 năm.',
        },
      },
    },
    footer: {
      manageCookies: 'Quản lý Cookie',
      doNotSell: 'Không bán thông tin của tôi',
    },
  },
};

export const getConsentTranslation = (language: string): ConsentTranslations => {
  return consentTranslations[language] || consentTranslations.en;
};
